import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, FileArrowUp, ClipboardText, CheckCircle, Warning } from '@phosphor-icons/react';

const FIELDS = ['date', 'breakfast', 'lunch', 'dinner', 'snacks', 'notes'];

const normDate = (v) => {
  v = String(v ?? '').trim();
  if (!v) return '';
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(v)) {
    const [y, m, d] = v.split('-');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const m = v.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = '20' + y;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  if (/^\d{5}$/.test(v)) {
    const dt = new Date(Math.round((+v - 25569) * 86400000));
    return isNaN(dt) ? '' : dt.toISOString().slice(0, 10);
  }
  const dt = new Date(v);
  return isNaN(dt) ? '' : dt.toISOString().slice(0, 10);
};

const splitCsvLine = (line) => {
  const out = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
    else if (c === ',' && !inQ) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
};

const gridToRows = (grid) => {
  if (!grid.length) return [];
  const first = grid[0].map(c => String(c ?? '').trim().toLowerCase());
  const hasHeader = first.some(c => ['date', 'day', 'breakfast', 'lunch', 'dinner', 'snack', 'snacks', 'notes'].includes(c));
  let map;
  if (hasHeader) {
    map = {};
    first.forEach((h, i) => {
      if (h.includes('date') || h === 'day') map.date = i;
      else if (h.startsWith('breakfast')) map.breakfast = i;
      else if (h.startsWith('lunch')) map.lunch = i;
      else if (h.startsWith('dinner')) map.dinner = i;
      else if (h.startsWith('snack') || h.includes('tea')) map.snacks = i;
      else if (h.startsWith('note')) map.notes = i;
    });
  } else {
    map = { date: 0, breakfast: 1, lunch: 2, dinner: 3, snacks: 4, notes: 5 };
  }
  const body = hasHeader ? grid.slice(1) : grid;
  return body
    .filter(r => r.some(c => String(c ?? '').trim()))
    .map(r => {
      const row = {};
      FIELDS.forEach(f => { row[f] = map[f] !== undefined ? String(r[map[f]] ?? '').trim() : ''; });
      row.date = normDate(row.date);
      return row;
    });
};

export default function ImportPlansModal({ api, plans, onClose, onUpdate }) {
  const [rows, setRows] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [importing, setImporting] = useState(false);
  const existingDates = new Set((plans || []).map(p => p.date));

  const parseText = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return;
    const grid = lines.map(l => l.includes('\t') ? l.split('\t') : splitCsvLine(l));
    const parsed = gridToRows(grid);
    if (!parsed.length) { toast.error('Could not find any rows in the pasted data'); return; }
    setRows(parsed);
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
        const parsed = gridToRows(grid);
        if (!parsed.length) { toast.error('No rows found in the Excel sheet'); return; }
        setRows(parsed);
      } else {
        parseText(await file.text());
      }
    } catch (e) { console.error(e); toast.error('Could not read that file'); }
  };

  const validRows = (rows || []).filter(r => r.date);
  const invalidCount = (rows || []).length - validRows.length;

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await api.post('/meals/plans/import', { rows: validRows });
      toast.success(`Imported ${r.data.imported} new, updated ${r.data.updated} plan(s) ✅`);
      onUpdate();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl p-5 max-h-[85vh] overflow-y-auto" data-testid="import-plans-modal"
        style={{ background: '#16181d', border: '1px solid rgba(255,255,255,0.1)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-white font-bold text-base flex items-center gap-2"><FileArrowUp size={18} className="text-emerald-300" /> Import Meal Plans</h2>
          <button onClick={onClose} data-testid="import-plans-close" className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          Columns: <span className="text-neutral-300">Date, Breakfast, Lunch, Dinner, Snacks, Notes</span> (header row optional). Dates like 2026-06-15 or 15/06/2026 both work. Existing days get updated.
        </p>

        {!rows ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-1.5"><ClipboardText size={12} /> Paste from Excel / Google Sheets</span>
              <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                onPaste={e => { const t = e.clipboardData.getData('text'); if (t.trim()) { e.preventDefault(); setPasteText(t); parseText(t); } }}
                rows={6} data-testid="import-paste-area"
                placeholder={'Copy cells in Excel and paste here…\nDate\tBreakfast\tLunch\tDinner\n15/06/2026\tPoha\tDal chawal\tRoti sabzi'}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white font-mono placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50" />
            </label>
            <div className="flex items-center gap-3">
              {pasteText.trim() && (
                <button onClick={() => parseText(pasteText)} data-testid="import-parse-btn"
                  className="px-4 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-xs font-bold">Preview pasted data</button>
              )}
              <label className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-neutral-300 text-xs font-bold cursor-pointer hover:text-white" data-testid="import-file-label">
                Or upload .xlsx / .csv
                <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden" data-testid="import-file-input"
                  onChange={e => handleFile(e.target.files?.[0])} />
              </label>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-xs">
              <span className="text-emerald-300 font-bold flex items-center gap-1"><CheckCircle size={13} weight="fill" /> {validRows.length} row(s) ready</span>
              {invalidCount > 0 && <span className="text-amber-300 flex items-center gap-1"><Warning size={13} /> {invalidCount} skipped (bad date)</span>}
              <button onClick={() => { setRows(null); setPasteText(''); }} data-testid="import-back-btn" className="ml-auto text-neutral-400 hover:text-white underline">← Start over</button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/10 max-h-[45vh] overflow-y-auto" data-testid="import-preview-table">
              <table className="w-full min-w-[640px] text-xs">
                <thead className="sticky top-0" style={{ background: '#1d2026' }}>
                  <tr>
                    <th className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wider font-bold text-neutral-500">Date</th>
                    {['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Notes'].map(h => (
                      <th key={h} className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wider font-bold text-neutral-500">{h}</th>
                    ))}
                    <th className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wider font-bold text-neutral-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-t border-white/5 ${!r.date ? 'opacity-40' : ''}`}>
                      <td className="px-2.5 py-1.5 text-neutral-200 whitespace-nowrap">{r.date || '—'}</td>
                      {['breakfast', 'lunch', 'dinner', 'snacks', 'notes'].map(f => (
                        <td key={f} className="px-2.5 py-1.5 text-neutral-300 max-w-[140px] truncate" title={r[f]}>{r[f] || <span className="text-neutral-600">—</span>}</td>
                      ))}
                      <td className="px-2.5 py-1.5 whitespace-nowrap">
                        {!r.date ? <span className="text-amber-400 font-bold">Skipped</span>
                          : existingDates.has(r.date) ? <span className="text-blue-300 font-bold">Update</span>
                            : <span className="text-emerald-300 font-bold">New</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-neutral-300 text-xs font-bold">Cancel</button>
              <button onClick={doImport} disabled={importing || validRows.length === 0} data-testid="import-confirm-btn"
                className="px-5 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold disabled:opacity-50">
                {importing ? 'Importing…' : `Import ${validRows.length} day(s)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
